import { FieldPath, UseFormReturn } from "react-hook-form";

import { SaveIndicator } from "./SaveIndicator";
import { useSaveIndicator } from "@/hooks/useSaveIndicator";
import { InfoTooltip } from "@/components/form/InfoTooltip";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { cn } from "@/lib/utils";

export const AutosaveFormEnumSelect = <T extends Record<string, unknown>>({
  className,
  emptyMessage,
  fieldName,
  form,
  infoTooltip,
  label,
  onSelect,
  options,
}: {
  className?: string;
  emptyMessage?: string;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  infoTooltip?: string;
  label: string;
  onSelect: (value: string) => Promise<void> | void;
  options: { value: string; label: string }[];
}) => {
  const { status, trigger } = useSaveIndicator();

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        return (
          <FormItem className={cn("min-w-0 text-slate-700", className)}>
            <FormLabel className="inline-flex w-full items-center justify-between">
              <span className="inline-flex min-w-0 items-center gap-1">
                <span className="truncate">{label}</span>
                {infoTooltip && (
                  <InfoTooltip className="shrink-0" content={infoTooltip} />
                )}
              </span>
              <SaveIndicator status={status} />
            </FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
                trigger(onSelect(value));
              }}
              value={(field.value as string) || undefined}
            >
              <FormControl>
                <SelectTrigger className="bg-slate-50 text-black focus:border-slate-400">
                  <span className="truncate">
                    <SelectValue placeholder="-" />
                  </span>
                </SelectTrigger>
              </FormControl>
              <SelectContent className="text-slate-700">
                {options.length > 0 ? (
                  options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))
                ) : emptyMessage ? (
                  <p className="px-2 py-1.5 text-sm text-slate-500">
                    {emptyMessage}
                  </p>
                ) : null}
              </SelectContent>
            </Select>

            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
